/**
 * Fleet Home Component
 * ====================
 * Entry point for fleet management. Shows fleet list or create fleet form
 * if user has no fleets yet.
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Truck,
  Plus,
  ChevronRight,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { fleetApi } from '../../services/fleetApi';
import Button from '../common/Button';
import Card from '../common/Card';
import FormField, { Input } from '../common/FormField';
import PageHeader from '../common/PageHeader';

// Create Fleet Form Component
const CreateFleetForm = ({ onSuccess }) => {
  const [fleetName, setFleetName] = useState('');
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();

  const createFleetMutation = useMutation({
    mutationFn: async (data) => {
      // fleetApi interceptor already extracts response.data
      const fleet = await fleetApi.createFleet(data);
      console.log('Created fleet:', fleet);
      return fleet;
    },
    onSuccess: (fleet) => {
      queryClient.invalidateQueries({ queryKey: ['fleets'] });
      onSuccess(fleet);
    },
    onError: (err) => {
      console.error('Create fleet error:', err);
      setError(err.message || 'Failed to create fleet');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!fleetName.trim()) {
      setError('Please enter a fleet name');
      return;
    }
    setError(null);
    // Use the user's existing subscription tier from their account
    createFleetMutation.mutate({ name: fleetName });
  };

  // Was a full-bleed ink-to-accent gradient with translucent glass panels: two
  // banned patterns, and a screen that looked like a different product from
  // the shell around it. Now the standard first-run card on the page surface.
  return (
    <div className="max-w-lg mx-auto py-8 lg:py-16">
      <div className="mb-6">
        <div className="w-12 h-12 rounded-control bg-surface-sunken flex items-center justify-center mb-4">
          <Truck className="w-6 h-6 text-ink-soft" strokeWidth={1.5} aria-hidden="true" />
        </div>
        <h1 className="text-page-title font-display text-ink">Create Your Fleet</h1>
        <p className="text-body text-ink-muted mt-1">
          Set up your fleet to start managing your vehicles, drivers, and maintenance
        </p>
      </div>

      <Card className="lg:p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          <FormField label="Fleet Name" htmlFor="fleet-name" error={error}>
            <Input
              id="fleet-name"
              type="text"
              value={fleetName}
              onChange={(e) => setFleetName(e.target.value)}
              placeholder="e.g., Main Fleet, Delivery Vehicles, Service Trucks"
              aria-invalid={error ? 'true' : undefined}
              autoFocus
            />
          </FormField>

          <Button
            type="submit"
            icon={Plus}
            loading={createFleetMutation.isPending}
            className="w-full"
          >
            {createFleetMutation.isPending ? 'Creating Fleet...' : 'Create Fleet'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

// Fleet Card Component
const FleetCard = ({ fleet }) => {
  // Capitalize the tier name for display
  const tierDisplay = fleet.subscription_tier 
    ? fleet.subscription_tier.charAt(0).toUpperCase() + fleet.subscription_tier.slice(1).replace('_', ' ')
    : 'Starter';

  return (
    <Link
      to={`/fleet/${fleet.id}`}
      className="block bg-surface-card rounded-card border border-border-default p-5 transition-colors duration-state hover:border-border-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 group"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start">
          <div className="p-3 bg-surface-sunken rounded-control">
            <Truck className="w-6 h-6 text-ink-soft" strokeWidth={1.5} aria-hidden="true" />
          </div>
          <div className="ml-4">
            <h3 className="text-card-title font-display text-ink group-hover:text-accent transition-colors duration-state">
              {fleet.name}
            </h3>
            <p className="text-small text-ink-muted mt-1">
              {tierDisplay} Plan • <span className="font-mono tabular-nums">{fleet.vehicle_count || 0}</span> vehicles
            </p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-ink-muted group-hover:text-accent transition-colors duration-state" strokeWidth={1.5} aria-hidden="true" />
      </div>
      
      <div className="mt-4 pt-4 border-t border-border-default grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-section-title font-mono tabular-nums text-ink">{fleet.vehicle_count || 0}</p>
          <p className="text-label uppercase text-ink-muted mt-1">Vehicles</p>
        </div>
        <div>
          <p className="text-section-title font-mono tabular-nums text-ink">{fleet.driver_count || 0}</p>
          <p className="text-label uppercase text-ink-muted mt-1">Drivers</p>
        </div>
        <div>
          <p className="text-section-title font-mono tabular-nums text-ink">{fleet.department_count || 0}</p>
          <p className="text-label uppercase text-ink-muted mt-1">Depts</p>
        </div>
      </div>
    </Link>
  );
};

// Main Fleet Home Component
const FleetHome = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch user's fleets
  // Note: fleetApi interceptor already extracts response.data
  const { data: fleetsData, isLoading, error, refetch } = useQuery({
    queryKey: ['fleets'],
    queryFn: async () => {
      try {
        const data = await fleetApi.listFleets();
        console.log('Fleets API data:', data);
        // fleetApi already returns response.data, so data is { fleets: [...], total: N }
        return data || { fleets: [], total: 0 };
      } catch (err) {
        console.error('Error fetching fleets:', err);
        return { fleets: [], total: 0 };
      }
    },
  });

  const fleets = fleetsData?.fleets || [];
  console.log('Fleets array:', fleets, 'Total:', fleetsData?.total);

  // Handle fleet creation success
  const handleFleetCreated = (fleet) => {
    console.log('Fleet created:', fleet);
    if (fleet && fleet.id) {
      navigate(`/fleet/${fleet.id}`);
    } else {
      // Fleet was created but we don't have the ID, just refresh the list
      refetch();
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-page flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-accent animate-spin mx-auto mb-4" />
          <p className="text-ink-muted">Loading your fleets...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-surface-page flex items-center justify-center p-4">
        <div className="bg-danger-tint border border-danger-border rounded-card p-6 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-danger mx-auto mb-4" />
          <h2 className="text-section-title font-display text-danger mb-2">Error Loading Fleets</h2>
          <p className="text-danger">{error.message}</p>
          <Button
            className="mt-4"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['fleets'] })}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // No fleets - show create form
  if (fleets.length === 0) {
    return <CreateFleetForm onSuccess={handleFleetCreated} />;
  }

  // Has fleets - show list
  return (
    <div>
      <div className="max-w-4xl mx-auto">
        <PageHeader
          title="Your Fleets"
          subtitle="Select a fleet to manage or create a new one"
          action={
            <Button icon={Plus} onClick={() => navigate('/fleet/new')}>
              New Fleet
            </Button>
          }
        />
      </div>

      {/* Fleet List */}
      <div className="max-w-4xl mx-auto">
        <div className="grid gap-4">
          {fleets.map((fleet) => (
            <FleetCard key={fleet.id} fleet={fleet} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FleetHome;
